"use client";

import React, { type ErrorInfo, type ReactNode, useState } from "react"
import { Button } from "@acme/ui/components/button";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter
} from "@acme/ui/components/card";
import {
    Alert,
    AlertTitle,
    AlertDescription
} from "@acme/ui/components/alert";
import {
    AlertCircle,
    RefreshCw,
    Home,
    Copy,
    Check,
    XCircle
} from "lucide-react";

interface ErrorBoundaryProps {
    isPreviewMode: boolean;
    children: ReactNode
}

interface ErrorBoundaryState {
    hasError: boolean
    error?: Error
    errorInfo?: ErrorInfo
}

const CopyButton = ({ text }: { text: string }) => {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={copyToClipboard}
            className="h-8 px-2"
        >
            {copied ? (
                <>
                    <Check className="h-4 w-4 mr-1" />
                    Copied
                </>
            ) : (
                <>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy Error Details
                </>
            )}
        </Button>
    );
};

class ClientErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false, error: undefined, errorInfo: undefined }
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error, errorInfo: undefined }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ hasError: true, error, errorInfo })
    }

    getFullErrorText(maxStackLines = 10) {
        const errorMessage = this.state.error?.toString() || "Unknown error";
        let stackTrace = this.state.errorInfo?.componentStack || "";

        // Limit stack trace depth
        if (stackTrace) {
            const stackLines = stackTrace.split('\n');
            if (stackLines.length > maxStackLines) {
                stackTrace = stackLines.slice(0, maxStackLines).join('\n') +
                    `\n\n... (${stackLines.length - maxStackLines} more lines truncated)`;
            }
        }

        return `${errorMessage}\n\nComponent Stack Trace:\n${stackTrace}`;
    }


    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }
        if (this.props.isPreviewMode) {
            // Detailed error view for preview mode
            const fullErrorText = this.getFullErrorText();

            return (
                <div className="flex items-center justify-center min-h-[50vh] p-4">
                    <Card className="w-full max-w-3xl shadow-lg border-destructive/20">
                        <CardHeader className="bg-destructive/5 border-b border-destructive/10">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <XCircle className="h-5 w-5 text-destructive" />
                                    <CardTitle className="text-destructive">Application Error</CardTitle>
                                </div>
                                <CopyButton text={fullErrorText} />
                            </div>
                            <CardDescription className="text-destructive/80 mt-2">
                                {this.state.error?.message || "An unexpected error occurred"}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-sm font-medium mb-1">Error Details</h3>
                                    <div className="bg-muted p-3 rounded-md text-sm font-mono overflow-auto max-h-[200px]">
                                        <pre className="whitespace-pre-wrap break-words">
                                            {this.state.error?.toString() || "Unknown error"}
                                        </pre>
                                    </div>
                                </div>

                                {this.state.errorInfo?.componentStack && (
                                    <div>
                                        <h3 className="text-sm font-medium mb-1">Stack Trace</h3>
                                        <div className="bg-muted p-3 rounded-md text-sm font-mono overflow-auto max-h-[300px]">
                                            <pre className="whitespace-pre-wrap break-words">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                        <CardFooter className="flex-col sm:flex-row gap-2 border-t pt-4 justify-center">
                            <Button
                                onClick={() => window.location.reload()}
                                className="w-full sm:w-auto"
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Reload Application
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => window.location.href = '/'}
                                className="w-full sm:w-auto"
                            >
                                <Home className="mr-2 h-4 w-4" />
                                Return Home
                            </Button>
                        </CardFooter>
                    </Card>
                </div>
            );
        }

        // Generic error view for production
        return (
            <div className="flex items-center justify-center min-h-[50vh] p-4">
                <Card className="w-full max-w-md shadow-lg">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 bg-destructive/10 p-3 rounded-full">
                            <AlertCircle className="h-8 w-8 text-destructive" />
                        </div>
                        <CardTitle>Something went wrong</CardTitle>
                        <CardDescription className="mt-2">
                            We're sorry, but we encountered an unexpected error.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                The application has encountered an error. Please try refreshing the page or return to the home page.
                            </AlertDescription>
                        </Alert>
                    </CardContent>
                    <CardFooter className="flex-col sm:flex-row gap-2 justify-center">
                        <Button
                            onClick={() => window.location.reload()}
                            className="w-full sm:w-auto"
                        >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Reload
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => window.location.href = '/'}
                            className="w-full sm:w-auto"
                        >
                            <Home className="mr-2 h-4 w-4" />
                            Go Home
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }
}

export default ClientErrorBoundary